<template>
  <div class="brokerage-notes" data-testid="brokerage-notes">
    <template v-if="isLoading">
      <SkeletonLoader class="brokerage-notes__loader brokerage-notes__wrapper">
        <div
          v-for="index in 6"
          :key="index"
          class="brokerage-notes__loader__row"
        />
      </SkeletonLoader>
    </template>
    <div v-else-if="hasError" class="brokerage-notes__wrapper">
      <HelperViewStatus
        class="brokerage-notes__helper"
        generic-error
        button-label="Tentar novamente"
        @click="getBrokeragesNotes"
      />
    </div>
    <div v-else-if="!hasBrokerageNotes" class="brokerage-notes__wrapper">
      <HelperViewContextual
        class="brokerage-notes__helper"
        icon="ED0030"
        description="Você não possui notas de negociação"
        context="neutral"
      />
    </div>
    <template v-else>
      <div
        v-for="month in months"
        :key="month.label"
        :class="[
          'brokerage-notes__accordion-wrapper',
          {
            'brokerage-notes__accordion-wrapper--opened':
              openedAccordionMonthsMap[month.label]
          }
        ]"
      >
        <Accordion
          class="brokerage-notes__accordion"
          :title="month.label"
          @accordion-changed="
            isOpen => handleToggleAccordion(isOpen, month.label)
          "
        >
          <BaseText
            v-if="month.brokerageNotes.length === 0"
            :color="NebraskaColors.elementSecondary"
          >
            Você não possui notas de negociação para este período
          </BaseText>
          <template v-else>
            <BrokerageNoteRow
              v-for="brokerageNote in month.brokerageNotes"
              :key="brokerageNote.date.value"
              :brokerage-note="brokerageNote"
              :disabled="isDownloadingBrokerageNote"
              :loading="
                currBrokerageNoteDownloading === brokerageNote.date.value
              "
              @on-click="handleDownloadBrokerageNote(brokerageNote)"
            />
          </template>
        </Accordion>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, onUnmounted, computed } from 'vue';

import {
  SkeletonLoader,
  HelperViewStatus,
  HelperViewContextual,
  BaseText
} from '@warrenbrasil/nebraska-web';
import { Accordion } from '@/modules/common/components/Accordion';
import { BrokerageNoteRow } from './components/BrokerageNoteRow';

import {
  PortfolioBrokerageNotesByMonth,
  getPortfolioBrokerageNotesList,
  PortfolioBrokerageNotesListFilters,
  PortfolioBrokerageNotesListFilterOption,
  PortfolioBrokerageNote
} from '@/modules/wealth/services/portfolio';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { getDailyBrokerageNote } from '@/modules/investments/services/trade';
import { forceDownloadHref } from '@/modules/common/helpers/force-download';
import { NotificationTypes } from '@/modules/common/services/http/notification';
import { notify } from '@/modules/common/helpers/notification';

interface Props {
  selectedYear?: number | string | null;
  portfolioId: string;
}
const props = defineProps<Props>();

interface Emits {
  (
    event: 'on-load',
    portfolioName?: string | null,
    selectedYear?: number | string,
    yearOptions?: PortfolioBrokerageNotesListFilterOption[]
  ): void;
}
const emit = defineEmits<Emits>();

const isLoading = ref(false);
const hasError = ref(false);
const filters = ref<PortfolioBrokerageNotesListFilters | null>(null);
const portfolioName = ref<string | null>(null);
const months = ref<PortfolioBrokerageNotesByMonth[]>([]);
const abortSignal = ref<AbortController | null>(null);
const currBrokerageNoteDownloading = ref<string | null>(null);
const openedAccordionMonthsMap = ref<Record<string, boolean>>({});

watch(
  () => props.selectedYear,
  async () => {
    const currentYearOfFetchedData = getSelectedFilterOption(
      filters.value?.years || []
    )?.value;
    if (currentYearOfFetchedData !== props.selectedYear || hasError.value)
      await getBrokeragesNotes();
  }
);

const hasBrokerageNotes = computed(() => months.value.length > 0);

const isDownloadingBrokerageNote = computed(() => {
  return currBrokerageNoteDownloading.value !== null;
});

const downloadResource = async (url: string, filename: string) => {
  const data = await fetch(url, {
    headers: new Headers({
      Origin: location.origin
    }),
    mode: 'cors'
  });

  const blob = await data.blob();

  const blobUrl = window.URL.createObjectURL(blob);
  forceDownloadHref(blobUrl, filename);
};

const handleDownloadBrokerageNote = async (
  brokerageNote: PortfolioBrokerageNote
) => {
  currBrokerageNoteDownloading.value = brokerageNote.date.value;

  try {
    const data = await getDailyBrokerageNote(brokerageNote.file.url);

    await downloadResource(data.generatedURL, brokerageNote.date.formatted);
  } catch {
    notify({
      group: 'wrn-notifications',
      text: 'Não foi possível iniciar o download',
      type: NotificationTypes.error
    });
  } finally {
    currBrokerageNoteDownloading.value = null;
  }
};

const handleToggleAccordion = (isOpen: boolean, month: string) => {
  openedAccordionMonthsMap.value[month] = isOpen;
};

const getSelectedFilterOption = (
  options: PortfolioBrokerageNotesListFilterOption[]
) => {
  return options.find(option => option.active);
};

const setOpenedAccordionMonthsMap = () => {
  const map: Record<string, boolean> = {};

  months.value.forEach(month => {
    map[month.label] = false;
  });

  openedAccordionMonthsMap.value = map;
};

const getBrokeragesNotes = async () => {
  if (abortSignal.value) {
    abortSignal.value.abort();
  }
  abortSignal.value = new AbortController();

  try {
    isLoading.value = true;
    hasError.value = false;

    const params = {
      portfolioId: props.portfolioId,
      year: props.selectedYear
    };

    const data = await getPortfolioBrokerageNotesList(
      params,
      abortSignal.value.signal
    );

    portfolioName.value = data.portfolio.name;
    filters.value = data.filters;
    months.value = data.months;

    setOpenedAccordionMonthsMap();

    const selectedYear = getSelectedFilterOption(
      filters.value?.years || []
    )?.value;
    emit('on-load', portfolioName.value, selectedYear, filters.value?.years);
  } catch {
    emit('on-load');

    hasError.value = true;
  } finally {
    isLoading.value = false;
  }
};

onMounted(async () => {
  await getBrokeragesNotes();
});

onUnmounted(() => {
  if (abortSignal.value) {
    abortSignal.value.abort();
  }
});
</script>

<style lang="less" scoped>
.brokerage-notes {
  &__wrapper {
    max-width: calc(800px + 2 * @size-spacing-x400);
    width: 100%;
    margin: 0 auto;
    padding: 0 @size-spacing-x400 @size-spacing-x600 @size-spacing-x400;

    .from-md({
      max-width: calc(800px + 2 * @size-spacing-x800);
      padding: 0 @size-spacing-x800 @size-spacing-x800 @size-spacing-x800;
    });
  }

  &__loader {
    display: flex;
    flex-direction: column;

    &__row {
      height: 29px;
      width: 100%;
      border-radius: @size-radius-x100;
      margin: @size-spacing-x350 0;
    }
  }

  &__helper {
    margin: @size-spacing-x800 auto 0 auto;
  }

  &__accordion {
    max-width: 800px;
    margin: 0 auto;

    :deep(button p) {
      padding: @size-spacing-x350 0 !important;
    }

    &-wrapper {
      width: 100%;
      padding: 0 @size-spacing-x400;

      .from-md({
        padding: 0 @size-spacing-x800;
      });

      &--opened {
        background-color: @background-primary;
      }
    }

    :deep(.wrn-accordion-text) {
      margin-bottom: 0 !important;
    }
  }
}
</style>
